/**
 * URL-backed state hooks.
 *
 * Why: in-page state (tab choice, drawer selection, search filters,
 * pagination) doesn't survive browser back, doesn't reset when the user
 * clicks the same left-nav link, and isn't shareable. Storing it in the
 * URL fixes all three.
 *
 * Two hooks:
 *   - useUrlParam(name, default)        : single named search param
 *   - useUrlEnum(name, allowed, default): same, but the value is type-narrowed
 *                                         to a literal-union and validated
 *                                         (unknown values fall back to default)
 *
 * Both write via `setSearchParams` so React Router pushes a history entry,
 * which makes browser back work.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useUrlParam(name: string, defaultValue = ''): [string, (next: string) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(name);
  const value = raw ?? defaultValue;
  const set = useCallback(
    (next: string) => {
      const merged = new URLSearchParams(params);
      if (!next || next === defaultValue) merged.delete(name);
      else merged.set(name, next);
      setParams(merged, { replace: false });
    },
    [name, defaultValue, params, setParams],
  );
  return [value, set];
}

/**
 * URL-backed enum state: returns a value that's guaranteed to be one of
 * `allowed`, falling back to `defaultValue` when the URL contains
 * something else (or nothing).
 */
export function useUrlEnum<T extends string>(
  name: string,
  allowed: readonly T[],
  defaultValue: T,
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(name);
  const value: T = useMemo(
    () => (allowed.includes(raw as T) ? (raw as T) : defaultValue),
    [raw, allowed, defaultValue],
  );
  const set = useCallback(
    (next: T) => {
      const merged = new URLSearchParams(params);
      if (next === defaultValue) merged.delete(name);
      else merged.set(name, next);
      setParams(merged, { replace: false });
    },
    [name, defaultValue, params, setParams],
  );
  return [value, set];
}

/** URL-backed integer (used for offset/page). */
export function useUrlInt(name: string, defaultValue = 0): [number, (next: number) => void] {
  const [raw, setRaw] = useUrlParam(name, String(defaultValue));
  const value = Number.isFinite(Number(raw)) ? Number(raw) : defaultValue;
  const set = useCallback((next: number) => setRaw(String(next)), [setRaw]);
  return [value, set];
}

/**
 * Debounced URL-backed string for search inputs. Returns:
 *   [draft, setDraft, committed]
 *
 *   draft     — local-state input value (binds to <input value>)
 *   setDraft  — setter for typing
 *   committed — debounced value after `delayMs`, mirrored to the URL
 *               (with replace: true so typing doesn't bloat history).
 *               Pass `committed` to your data hook so the API only refires
 *               after the user stops typing.
 *
 * On mount, draft is seeded from the URL so deep-links / refreshes restore
 * the search state.
 */
export function useDebouncedUrlParam(
  name: string,
  delayMs = 250,
): [string, (next: string) => void, string] {
  const [params, setParams] = useSearchParams();
  const initialFromUrl = params.get(name) ?? '';
  const [draft, setDraft] = useState<string>(initialFromUrl);
  const [committed, setCommitted] = useState<string>(initialFromUrl);

  // When the URL changes externally (e.g. user clicks a Reset link),
  // re-seed both draft + committed.
  useEffect(() => {
    const fromUrl = params.get(name) ?? '';
    if (fromUrl !== committed) {
      setDraft(fromUrl);
      setCommitted(fromUrl);
    }
  }, [params.get(name)]);

  // Debounce: mirror draft → URL + committed after `delayMs`.
  useEffect(() => {
    if (draft === committed) return;
    const t = window.setTimeout(() => {
      setCommitted(draft);
      const merged = new URLSearchParams(params);
      if (draft) merged.set(name, draft);
      else merged.delete(name);
      setParams(merged, { replace: true });
    }, delayMs);
    return () => window.clearTimeout(t);
  }, [draft, delayMs]);

  return [draft, setDraft, committed];
}
